import { useState } from "react";

const topPicks = [
  {
    id: "linen-oat",
    name: "Oat Linen Cushion Cover",
    description: "A classic linen cover in warm oat — the most versatile pillow in a calm neutral room. Works with everything.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Linen Pillows",
    icon: "ri-contrast-drop-line",
    tag: "Editor's Pick",
    affiliateUrl: "",
  },
  {
    id: "boucle-ivory",
    name: "Ivory Boucle Cushion",
    description: "A looped boucle texture in warm ivory — sculptural, tactile, and endlessly photogenic on a beige sofa.",
    priceRange: "Mid-range",
    buttonLabel: "See Boucle Options",
    icon: "ri-contrast-drop-line",
    tag: "Most Textured",
    affiliateUrl: "",
  },
  {
    id: "lumbar-sand",
    name: "Sand Linen Lumbar Pillow",
    description: "A warm sand lumbar that sits beautifully at the front of a sofa arrangement — the finishing layer.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "Explore Lumbar Pillows",
    icon: "ri-contrast-drop-line",
    tag: "Best Lumbar",
    affiliateUrl: "",
  },
];

const budgetPillows = [
  {
    id: "b1",
    name: "Affordable Linen-Look Cushion Covers",
    description: "Budget-friendly covers in warm neutral tones — easy to swap seasonally and surprisingly stylish.",
    priceRange: "Under $25 per cover",
    buttonLabel: "View Budget Options",
    icon: "ri-contrast-drop-line",
    tag: "Best Value",
    affiliateUrl: "",
  },
  {
    id: "b2",
    name: "Simple Cotton Weave Cushion",
    description: "A clean woven cotton in warm ivory — minimal, soft, and a great base for a layered arrangement.",
    priceRange: "Under $35",
    buttonLabel: "See Affordable Picks",
    icon: "ri-contrast-drop-line",
    tag: "Smart Pick",
    affiliateUrl: "",
  },
];

const midPillows = [
  {
    id: "m1",
    name: "Quality Linen Cushion Cover Set",
    description: "A set of two linen covers in complementary warm neutrals — the easiest way to start a considered arrangement.",
    priceRange: "$40–$80 per cover",
    buttonLabel: "View Linen Sets",
    icon: "ri-contrast-drop-line",
    tag: "Most Versatile",
    affiliateUrl: "",
  },
  {
    id: "m2",
    name: "Textured Boucle Cushion",
    description: "A mid-range boucle with excellent texture and a warm ivory tone — the piece that elevates the whole sofa.",
    priceRange: "$50–$100",
    buttonLabel: "Explore Boucle Picks",
    icon: "ri-contrast-drop-line",
    tag: "Texture Hero",
    affiliateUrl: "",
  },
];

const splurgePillows = [
  {
    id: "s1",
    name: "Belgian Linen Cushion Cover",
    description: "Premium Belgian linen with exceptional drape and a warm natural tone — the gold standard for a calm room.",
    priceRange: "$120+ per cover",
    buttonLabel: "View Luxury Pillows",
    icon: "ri-contrast-drop-line",
    tag: "Heirloom Quality",
    affiliateUrl: "",
  },
  {
    id: "s2",
    name: "Hand-Woven Artisan Cushion",
    description: "A hand-woven cushion in warm sand and ivory — artisan-made and quietly beautiful.",
    priceRange: "$150+",
    buttonLabel: "Explore Artisan Picks",
    icon: "ri-contrast-drop-line",
    tag: "Artisan Craft",
    affiliateUrl: "",
  },
];

const pairWith = [
  {
    id: "sofa",
    name: "Beige Linen Sofa",
    description: "The natural home for your throw pillows — a warm beige sofa that lets the cushions do the work.",
    priceRange: "Mid-range",
    buttonLabel: "View Sofa Options",
    icon: "ri-sofa-line",
    tag: "Perfect Pair",
    affiliateUrl: "https://amzn.to/4sd5Qyh",
  },
  {
    id: "throw",
    name: "Linen or Knit Throw Blanket",
    description: "A loosely draped throw over one arm completes the layered look and adds another texture.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Throw Options",
    icon: "ri-contrast-drop-line",
    tag: "Easy Layer",
    affiliateUrl: "",
  },
  {
    id: "rug",
    name: "Neutral Area Rug",
    description: "A soft neutral rug underneath grounds the sofa and ties the whole arrangement together.",
    priceRange: "Budget to Mid-range",
    buttonLabel: "View Rug Options",
    icon: "ri-layout-grid-line",
    tag: "Essential Layer",
    affiliateUrl: "https://amzn.to/4dx4Ykf",
  },
  {
    id: "table",
    name: "Light Wood Coffee Table",
    description: "A warm wood table in front of the sofa anchors the seating area and completes the calm room.",
    priceRange: "Mid-range",
    buttonLabel: "Explore Coffee Tables",
    icon: "ri-table-line",
    tag: "Finishing Touch",
    affiliateUrl: "https://amzn.to/4tANU1D",
  },
];

const faqs = [
  { q: "How many throw pillows should I put on a sofa?", a: "For a two-seater: 4–5 pillows. For a three-seater: 5–7. Odd numbers almost always look more natural and relaxed than even numbers. The key is to have enough pillows to create a layered look without overcrowding the sofa — you still need room to actually sit." },
  { q: "What sizes should I choose?", a: "A mix of sizes works best: 60x60cm as the main back pillows, 45x45cm as secondary pillows, and a lumbar (30x50cm) at the front. This creates a natural layered effect that looks considered rather than staged." },
  { q: "Should I use pillow covers or complete pillows?", a: "Covers are almost always the better choice. They are easier to wash, easier to swap seasonally, and much more cost-effective. Invest in good feather-down inserts once, then change the covers as your taste evolves." },
  { q: "How do I mix textures without it looking messy?", a: "Keep the palette tight — no more than three tones within the same warm neutral family. Then vary the texture freely within that palette: smooth linen, looped boucle, woven cotton. Same colour family, different surfaces. That is the formula." },
  { q: "What is the best fill for throw pillows?", a: "Feather-down inserts give the best shape, the most luxurious feel, and that slightly overstuffed look that photographs so well. Foam inserts tend to look stiff and boxy. If you prefer a vegan option, a high-quality hollow fibre insert is the next best thing." },
];

function ProductCard({ name, description, priceRange, buttonLabel, icon, tag, affiliateUrl }: {
  name: string; description: string; priceRange: string; buttonLabel: string;
  icon: string; tag: string; affiliateUrl: string;
}) {
  const [hovered, setHovered] = useState(false);
  const hasLink = affiliateUrl.trim().length > 0;

  return (
    <div
      className="border border-[#EDE6DA] rounded-xl p-6 bg-[#FDFAF5] hover:border-[#C8BAA8] transition-all duration-300 flex flex-col gap-4"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F1E8]">
          <i className={`${icon} text-[#8B8378] text-base`} />
        </div>
        <span className="text-[#B5A898] text-[9px] tracking-[0.35em] uppercase border border-[#EDE6DA] px-2.5 py-1 rounded-full" style={{ fontFamily: "'Inter', sans-serif" }}>{tag}</span>
      </div>
      <h3 className="text-[#2C2825] text-lg leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>{name}</h3>
      <p className="text-[#8B8378] text-sm leading-relaxed flex-1" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{description}</p>
      <div className="flex items-center gap-2">
        <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>Price</span>
        <span className="w-4 h-px bg-[#EDE6DA]" />
        <span className="text-[#6B5E53] text-xs" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{priceRange}</span>
      </div>
      {hasLink ? (
        <a
          href={affiliateUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#C8BAA8] text-[#6B5E53] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] hover:bg-[#2C2825] hover:text-white hover:border-[#2C2825] transition-all duration-300 cursor-pointer whitespace-nowrap"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`ri-arrow-right-line text-[10px] transition-transform duration-200 ${hovered ? "translate-x-0.5" : ""}`} />
          </span>
        </a>
      ) : (
        <div
          className="mt-1 self-start inline-flex items-center gap-2 border border-[#EDE6DA] text-[#C8BAA8] px-5 py-2.5 rounded-full text-xs tracking-[0.14em] whitespace-nowrap select-none"
          style={{ fontFamily: "'Inter', sans-serif" }}
          title="Link coming soon"
        >
          {buttonLabel}
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-[10px]" /></span>
        </div>
      )}
    </div>
  );
}

function Grid2({ items }: { items: typeof budgetPillows }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{items.map(p => <ProductCard key={p.id} {...p} />)}</div>;
}

function Grid3({ items }: { items: typeof topPicks }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">{items.map(p => <ProductCard key={p.id} {...p} />)}</div>;
}

function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-5 mb-10">
      <div className="flex-1 h-px bg-[#EDE6DA]" />
      <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</p>
      <div className="flex-1 h-px bg-[#EDE6DA]" />
    </div>
  );
}

export default function TPShopSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <section className="max-w-2xl mx-auto px-4 md:px-0 py-4 mb-16">

      <div id="top-picks" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Pillow Edit</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Our Top Beige Throw Pillow Picks</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          These are the pillows we keep recommending — chosen for their warm tone, natural material, and ability to layer beautifully without competing with each other.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid3 items={topPicks} />
      </div>

      <Divider label="By Budget" />

      <div id="budget-pillows" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Budget</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Best Beige Pillows Under $35</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          A considered pillow arrangement does not have to be expensive. These picks keep the tone warm and the look intentional.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={budgetPillows} />
      </div>

      <div id="mid-pillows" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Mid-Range</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Elevated Pillows $40–$100</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          In this range, fabric quality and texture improve noticeably. These pillows feel genuinely luxurious and photograph beautifully.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={midPillows} />
      </div>

      <div id="splurge-pillows" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Luxury</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Splurge Picks — Luxury Throw Pillows</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          For a more elevated result, these pillows offer exceptional fabric quality, artisan construction, and the kind of quiet presence that makes a sofa feel complete.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={splurgePillows} />
      </div>

      <Divider label="Complete the Look" />

      <div id="pair-with" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Style It</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>What to Pair With Beige Throw Pillows</h2>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-2" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Throw pillows work best when the rest of the room stays warm and layered. These are the pieces that complete the look.
        </p>
        <div className="w-12 h-px bg-[#C8BAA8] mb-10" />
        <Grid2 items={pairWith} />
      </div>

      {/* FAQ */}
      <div id="faq" className="mb-16">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>FAQ</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>Frequently Asked Questions</h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-[#EDE6DA] rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F5F1E8]/50 transition-colors duration-200"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="text-[#2C2825] text-sm leading-snug pr-4" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{faq.q}</span>
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`ri-add-line text-[#8B8378] text-base transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`} />
                </span>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-5">
                  <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Conclusion */}
      <div className="mb-4">
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          The right throw pillows are one of the simplest upgrades you can make to a calm room.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Keep the palette warm and tight, mix at least two textures, vary the sizes, and resist the urge to over-coordinate.
          The goal is a sofa that looks effortlessly layered — like it came together naturally, not like it was styled for a photoshoot.
        </p>
      </div>

    </section>
  );
}
